import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { DataGrid } from '@/components/DataGrid/DataGrid';
import { useServerDataGrid } from '@/components/DataGrid/useServerDataGrid';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { Permission } from '@/auth/permissions';
import { useAuth } from '@/auth/useAuth';
import { SpriteIcon } from '@/components/SpriteIcon';
import { useAlert, useConfirm } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { UserDetailsModal } from './UserDetailsModal';
import { UserFormModal, type UserFormValues } from './UserFormModal';
import {
  useCreateUser,
  useDeleteUser,
  useLockUser,
  useSendAccessEmail,
  useUnlockUser,
  useUpdateUser,
  userDisplayName,
  userKeys,
  userStatus,
  useUsersBootstrap,
  type UserListItem,
} from './useUsers';

/**
 * Replaces `Pages/Auth/Users.cshtml` at `/auth/users`.
 *
 * Server-paged, as the Razor page was — the account table is the one admin grid
 * that can grow without bound. The three filters and the four tiles above it
 * carry over unchanged.
 *
 * What the user may do is decided from their own `Permission` claims via
 * `useAuth`, not from flags in the payload: the page model computed `CanCreate`,
 * `CanEdit`, `CanDelete`, `CanLock` and `CanResetPassword` server-side and
 * rendered them into the markup, but the client already holds those claims from
 * `GET /api/auth/me`. The controller's `[Authorize]` attributes remain the
 * enforcement point either way; this only decides which buttons exist.
 */

const STATUS_OPTIONS = ['active', 'inactive', 'locked'] as const;

function formatDate(value: string | null): string {
  return value ? value.substring(0, 10) : '—';
}

/**
 * One row action: a glyph from the shared sprite, no visible text.
 *
 * The Razor rows rendered these as icon-only buttons and that is the approved
 * design, so the label is carried by `title` and `aria-label` rather than shown.
 * Six of them per row across a full page of users is the reason — spelled out,
 * the column would be wider than the data.
 */
function IconButton({
  variant,
  icon,
  label,
  onClick,
  disabled,
}: {
  variant: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'danger';
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`btn btn-outline-${variant}`}
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
    >
      <SpriteIcon name={icon} size={14} />
    </button>
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const confirm = useConfirm();
  const alert = useAlert();
  const toast = useToast();

  const can = {
    create: hasPermission(Permission.users.create),
    edit: hasPermission(Permission.users.edit),
    delete: hasPermission(Permission.users.delete),
    lock: hasPermission(Permission.users.lock),
    resetPassword: hasPermission(Permission.users.resetPassword),
  };

  const { data: bootstrap } = useUsersBootstrap();

  const [searchText, setSearchText] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 350);

  const filters = useMemo(() => ({ role, status }), [role, status]);

  const { gridProps, resetToFirstPage } = useServerDataGrid<UserListItem>({
    endpoint: '/api/users/query',
    queryKey: userKeys.grid,
    initialPageSize: 25,
    search: debouncedSearch,
    filters,
  });

  // Page 5 of the old result set means nothing in the new one.
  useEffect(() => resetToFirstPage(), [debouncedSearch, filters, resetToFirstPage]);

  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const lockUser = useLockUser();
  const unlockUser = useUnlockUser();
  const sendAccessEmail = useSendAccessEmail();

  const closeForm = useCallback(() => {
    setCreating(false);
    setEditing(null);
    createUser.reset();
    updateUser.reset();
  }, [createUser, updateUser]);

  const handleSubmit = useCallback(
    (values: UserFormValues) => {
      if (editing) {
        updateUser.mutate({ ...values, id: editing.id }, { onSuccess: closeForm });
      } else {
        createUser.mutate(values, { onSuccess: closeForm });
      }
    },
    [editing, updateUser, createUser, closeForm],
  );

  const handleDelete = useCallback(
    (user: UserListItem) => {
      const name = userDisplayName(user);
      // The request runs from inside the dialog, so a refusal from the server —
      // the last administrator, a row the account may not touch — is shown
      // there rather than disappearing the way it did behind window.confirm.
      void confirm({
        variant: 'danger',
        icon: 'trash-2',
        title: t('crud:Crud.Delete.ConfirmTitle', 'Confirm deletion'),
        message: t('Users.ConfirmDelete', '{{name}} will be permanently removed.', { name }),
        confirmLabel: t('Button.Delete', 'Delete'),
        onConfirm: () => deleteUser.mutateAsync(user.id),
      });
    },
    [confirm, deleteUser, t],
  );

  const handleSendAccessEmail = useCallback(
    (user: UserListItem) => {
      sendAccessEmail.mutate(user.id, {
        // Success is a toast: it confirms what the user just asked for, and an
        // administrator mailing several accounts in a row should not have to
        // dismiss a dialog between each one. A failure still does.
        onSuccess: () =>
          toast({
            variant: 'success',
            icon: 'mail',
            message: t('Users.AccessEmailSent', 'Access email sent to {{email}}.', {
              email: user.email ?? '',
            }),
          }),
        onError: () =>
          void alert({
            variant: 'danger',
            title: t('Message.Error', 'Error'),
            message: t('Users.AccessEmailFailed', 'Could not send the access email.'),
          }),
      });
    },
    [alert, toast, sendAccessEmail, t],
  );

  const columns = useMemo<ColumnDef<UserListItem, unknown>[]>(
    () => [
      {
        id: 'userName',
        accessorKey: 'userName',
        header: t('Users.User', 'User'),
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{userDisplayName(row.original)}</div>
            <div className="fs-sm text-muted">{row.original.userName}</div>
          </div>
        ),
      },
      { id: 'email', accessorKey: 'email', header: t('Users.Email', 'Email') },
      {
        id: 'roles',
        accessorKey: 'roles',
        header: t('Users.Roles', 'Roles'),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.roles.length === 0 ? (
            <span className="text-muted">—</span>
          ) : (
            <span className="d-inline-flex flex-wrap gap-1">
              {row.original.roles.map((r) => (
                <span className="badge bg-secondary" key={r}>
                  {r}
                </span>
              ))}
            </span>
          ),
      },
      {
        id: 'status',
        accessorKey: 'isActive',
        header: t('Users.Status', 'Status'),
        meta: { align: 'center' },
        cell: ({ row }) => {
          const value = userStatus(row.original);
          const className =
            value === 'locked' ? 'bg-danger' : value === 'inactive' ? 'bg-warning' : 'bg-success';
          return (
            <span className={`badge ${className}`}>
              {t(`Users.Status.${value}`, value)}
            </span>
          );
        },
      },
      {
        id: 'lastLoginDate',
        accessorKey: 'lastLoginDate',
        header: t('Users.LastLogin', 'Last login'),
        cell: ({ row }) => formatDate(row.original.lastLoginDate),
      },
      {
        id: 'createdDate',
        accessorKey: 'createdDate',
        header: t('Users.Created', 'Created'),
        cell: ({ row }) => formatDate(row.original.createdDate),
      },
      {
        id: 'actions',
        header: () => null,
        enableSorting: false,
        meta: { align: 'end' },
        cell: ({ row }) => {
          const user = row.original;
          const locked = userStatus(user) === 'locked';

          // Mirrors UserManagementService.SendAccessEmailAsync: an account that
          // is active and has signed in before gets a password reset; anything
          // else — including accounts imported as active but never onboarded —
          // gets an activation link. Only the tooltip changes; the server
          // decides which mail it actually sends.
          const willReset = user.isActive && user.lastLoginDate != null;

          return (
            <div className="btn-group btn-group-sm">
              <IconButton
                variant="primary"
                icon="eye"
                label={t('Users.ViewDetails', 'View details')}
                onClick={() => setViewing(user.id)}
              />
              {can.edit && (
                <IconButton
                  variant="secondary"
                  icon="edit"
                  label={t('Users.Edit', 'Edit')}
                  onClick={() => setEditing(user)}
                />
              )}
              {can.resetPassword && (
                <IconButton
                  variant="info"
                  icon="mail"
                  label={
                    willReset
                      ? t('Users.SendResetEmail', 'Send password reset email')
                      : t('Users.SendActivationEmail', 'Send activation email')
                  }
                  onClick={() => handleSendAccessEmail(user)}
                  disabled={sendAccessEmail.isPending}
                />
              )}
              {can.lock &&
                (locked ? (
                  <IconButton
                    variant="success"
                    icon="unlock"
                    label={t('Users.Unlock', 'Unlock')}
                    onClick={() => unlockUser.mutate(user.id)}
                  />
                ) : (
                  <IconButton
                    variant="warning"
                    icon="lock"
                    label={t('Users.Lock', 'Lock')}
                    onClick={() => lockUser.mutate(user.id)}
                  />
                ))}
              {can.delete && (
                <IconButton
                  variant="danger"
                  icon="trash-2"
                  label={t('Users.Delete', 'Delete')}
                  onClick={() => handleDelete(user)}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t, can.edit, can.delete, can.lock, can.resetPassword, handleDelete, handleSendAccessEmail, lockUser, unlockUser, sendAccessEmail.isPending],
  );

  const stats = bootstrap?.statistics;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h1 className="h3 mb-1">{t('Users.Title', 'Users')}</h1>
          <p className="text-muted mb-0">
            {t('Users.Subtitle', 'Accounts, roles and access to the dashboard.')}
          </p>
        </div>
        {can.create && (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            {t('Users.Create', 'Create user')}
          </button>
        )}
      </div>

      <div className="row g-3 mb-4">
        {[
          { key: 'total', label: t('Users.Total', 'Total users'), value: gridProps.total, tone: '' },
          { key: 'active', label: t('Users.Active', 'Active'), value: stats?.active, tone: 'text-success' },
          { key: 'inactive', label: t('Users.Inactive', 'Inactive'), value: stats?.inactive, tone: 'text-warning' },
          { key: 'locked', label: t('Users.Locked', 'Locked'), value: stats?.locked, tone: 'text-danger' },
        ].map((tile) => (
          <div className="col-6 col-lg-3" key={tile.key}>
            <div className="card shadow-sm h-100">
              <div className="card-body text-center">
                <h5 className="text-muted mb-2 fs-sm">{tile.label}</h5>
                <h2 className={`mb-0 ${tile.tone}`}>{tile.value ?? '—'}</h2>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h5 className="mb-0">{t('Users.Filters', 'Search filters')}</h5>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="users-search">
                {t('Users.Search', 'Search')}
              </label>
              <input
                id="users-search"
                className="form-control"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder={t('Users.SearchPlaceholder', 'Name, user name or email')}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="users-role">
                {t('Users.Role', 'Role')}
              </label>
              <select
                id="users-role"
                className="form-select"
                value={role}
                onChange={(event) => setRole(event.target.value)}
              >
                <option value="">{t('Users.AllRoles', 'All roles')}</option>
                {(bootstrap?.availableRoles ?? []).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="users-status">
                {t('Users.Status', 'Status')}
              </label>
              <select
                id="users-status"
                className="form-select"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">{t('Users.AllStatuses', 'All statuses')}</option>
                {STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {t(`Users.Status.${value}`, value)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          <DataGrid<UserListItem>
            {...gridProps}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50, 100]}
            emptyMessage={t('Users.Empty', 'No users match these filters')}
          />
        </div>
      </div>

      {viewing && <UserDetailsModal userId={viewing} onClose={() => setViewing(null)} />}

      {(creating || editing) && (
        <UserFormModal
          user={editing}
          availableRoles={bootstrap?.availableRoles ?? []}
          roleScopes={bootstrap?.roleScopes ?? {}}
          fullAccessRoles={bootstrap?.fullAccessRoles ?? []}
          pending={createUser.isPending || updateUser.isPending}
          error={createUser.error ?? updateUser.error}
          onCancel={closeForm}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}
